import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader } from "@/components/Loader";
import { CheckCircle2, XCircle, Clock, Check, X } from "lucide-react";

export default function Results() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [showReview, setShowReview] = useState(false);
  const [requesting, setRequesting] = useState(false);

  const load = () => api.get(`/nurse/results/${id}`).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, [id]);

  const requestReattempt = async () => {
    setRequesting(true);
    try { await api.post(`/nurse/results/${id}/reattempt`); toast.success("Reattempt requested. MITRA will review."); }
    catch (e) { toast.error(apiError(e)); }
    finally { setRequesting(false); }
  };

  if (!data) return <Loader />;

  return (
    <div className="min-h-screen bg-[#fcf9f8]">
      <div className="max-w-md mx-auto px-4 py-8 pb-16" data-testid="results-page">
        {data.status !== "graded" ? (
          <div className="bg-white rounded-lg border border-[#E5E7EB] p-8 text-center" data-testid="results-pending">
            <Clock className="w-12 h-12 text-[#F59E0B] mx-auto mb-3" />
            <h1 className="text-xl font-bold text-[#1c1b1b]">Awaiting Grading</h1>
            <p className="text-sm text-[#434654] mt-2">Your assessment includes short answers that MITRA will grade manually. You'll be notified.</p>
            <button onClick={() => navigate(`/app/modules/${data.module_id}`)} data-testid="back-to-module-btn"
              className="mt-6 w-full h-12 rounded-lg bg-[#003d9b] text-white font-semibold hover:bg-[#0052cc] transition-colors">
              Back to Module
            </button>
          </div>
        ) : (
          <>
            <div className={`rounded-lg p-8 text-center text-white ${data.passed ? "bg-[#004e33]" : "bg-[#ba1a1a]"}`} data-testid="results-summary">
              {data.passed ? <CheckCircle2 className="w-14 h-14 mx-auto mb-3" /> : <XCircle className="w-14 h-14 mx-auto mb-3" />}
              <h1 className="text-2xl font-bold">{data.passed ? "Passed" : "Not Passed"}</h1>
              <p className="text-[48px] font-bold leading-none mt-4" data-testid="final-score">{data.final_score_percent}%</p>
              <p className="text-sm text-white/80 mt-1">Passing score: {data.pass_threshold}%</p>
            </div>

            <div className="mt-5 space-y-3">
              <button onClick={() => setShowReview(!showReview)} data-testid="toggle-review-btn"
                className="w-full h-12 rounded-lg border border-[#E5E7EB] bg-white font-semibold text-[#003d9b] hover:bg-gray-50 transition-colors">
                {showReview ? "Hide Review" : "Review Answers"}
              </button>
              {data.passed ? (
                <button onClick={() => navigate("/app")} data-testid="continue-training-btn"
                  className="w-full h-12 rounded-lg bg-[#003d9b] text-white font-semibold hover:bg-[#0052cc] transition-colors">
                  Continue Training
                </button>
              ) : (
                <button onClick={requestReattempt} disabled={requesting} data-testid="request-reattempt-btn"
                  className="w-full h-12 rounded-lg bg-[#003d9b] text-white font-semibold hover:bg-[#0052cc] transition-colors disabled:opacity-50">
                  {requesting ? "Requesting…" : "Request Reattempt"}
                </button>
              )}
            </div>

            {showReview && (
              <div className="mt-6 space-y-4" data-testid="answer-review">
                {data.review.map((r, i) => (
                  <div key={i} className="bg-white rounded-lg border border-[#E5E7EB] p-4">
                    <p className="font-semibold text-[#1c1b1b] mb-2">{i + 1}. {r.question}</p>
                    {r.type === "mcq" ? (
                      <div className="space-y-1.5">
                        {r.options.map((opt, oi) => {
                          const isCorrect = oi === r.correct_index;
                          const isYour = oi === r.your_index;
                          return (
                            <div key={oi} className={`flex items-center gap-2 text-sm px-3 py-2 rounded-md ${
                              isCorrect ? "bg-[#004e33]/10 text-[#004e33]" : isYour ? "bg-[#ba1a1a]/10 text-[#ba1a1a]" : "text-[#434654]"
                            }`}>
                              {isCorrect ? <Check className="w-4 h-4" /> : isYour ? <X className="w-4 h-4" /> : <span className="w-4" />}
                              {opt}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-sm">
                        <p className="text-[#434654]">Your answer: <span className="text-[#1c1b1b]">{r.your_text || "—"}</span></p>
                        <p className="text-[#434654] mt-1">Awarded: {r.awarded}/{r.points} points</p>
                      </div>
                    )}
                    {r.explanation && (
                      <p className="text-xs text-[#434654] mt-2 pt-2 border-t border-[#E5E7EB]"><span className="font-semibold">Explanation:</span> {r.explanation}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
